import React, { useEffect, useState } from 'react';
import Cal, { getCalApi } from "@calcom/embed-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Calendar, Video, Clock, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hardcoded event types for Cal.eu (since API doesn't support listing)
const EVENT_TYPES = [
  { id: 1, title: "30 min meeting", slug: "30min", length: 30 },
  { id: 2, title: "15 min meeting", slug: "15min", length: 15 }
];

export default function CalComBookingModal({ 
  isOpen, 
  onClose, 
  contact,
  token,
  onBookingSuccess 
}) {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEventType, setSelectedEventType] = useState(EVENT_TYPES[0]);
  const [calInitialized, setCalInitialized] = useState(false);

  useEffect(() => {
    if (isOpen && token) {
      fetchEmbedConfig();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && embedConfig?.enabled && !calInitialized) {
      initCalEmbed();
    }
  }, [isOpen, embedConfig, calInitialized]);

  const fetchEmbedConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/calcom/embed-config?token=${token}`);
      const data = await response.json();
      setEmbedConfig(data);
    } catch (error) {
      console.error('Error fetching Cal.com config:', error);
    } finally {
      setLoading(false);
    }
  };

  const initCalEmbed = async () => {
    try {
      const cal = await getCalApi({"namespace":"crm-booking"});
      
      cal("ui", {
        theme: "light",
        styles: {
          branding: {
            brandColor: "#3b82f6",
          },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });

      // Listen for booking events
      cal("on", {
        action: "bookingSuccessful",
        callback: (e) => {
          console.log("Booking successful:", e.detail);
          // Save meeting to CRM
          saveMeetingToCRM(e.detail);
          if (onBookingSuccess) {
            onBookingSuccess(e.detail);
          }
        },
      });
      
      setCalInitialized(true);
    } catch (error) {
      console.error('Error initializing Cal.com:', error);
    }
  };

  const saveMeetingToCRM = async (bookingData) => {
    try {
      // The webhook will handle this, but we can also save directly
      console.log('Meeting booked:', bookingData);
    } catch (error) {
      console.error('Error saving meeting to CRM:', error);
    }
  };

  const getCalLink = () => {
    if (!embedConfig?.username || !selectedEventType?.slug) return null;
    return `${embedConfig.username}/${selectedEventType.slug}`;
  };

  const handleClose = () => {
    setCalInitialized(false);
    onClose();
  };

  if (!isOpen) return null;

  const calLink = getCalLink();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Schedule Meeting with {contact?.name || 'Contact'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : !embedConfig?.enabled ? (
          <div className="text-center py-12 px-4">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Cal.com Not Configured</h3>
            <p className="text-gray-500 mb-4">
              Please configure Cal.com integration in Settings to enable meeting scheduling.
            </p>
          </div>
        ) : (
          <div className="space-y-3 p-4 pt-2">
            {/* Event Type Selector */}
            <div className="flex gap-2 flex-wrap">
              {EVENT_TYPES.map((et) => (
                <Button
                  key={et.id}
                  variant={selectedEventType?.id === et.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSelectedEventType(et);
                    setCalInitialized(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Clock className="h-4 w-4" />
                  {et.title}
                </Button>
              ))}
            </div>

            {/* Contact Info */}
            <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{contact?.name}</p>
                <p className="text-sm text-gray-500">{contact?.email || 'No email'}</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Video className="h-4 w-4 text-green-600" />
                Google Meet
              </div>
            </div>

            {/* Cal.com Embed */}
            {calLink && embedConfig?.enabled && (
              <div 
                className="cal-embed-wrapper rounded-lg overflow-hidden border"
                style={{ height: '550px' }}
              >
                <Cal
                  namespace="crm-booking"
                  calLink={calLink}
                  calOrigin="https://cal.eu"
                  style={{ width: "100%", height: "100%", overflow: "auto" }}
                  config={{
                    layout: "month_view",
                    theme: "light",
                    // Pre-fill guest information
                    name: contact?.name || "",
                    email: contact?.email || "",
                    guests: contact?.email ? [contact.email] : [],
                    // Hide branding
                    hideEventTypeDetails: false,
                  }}
                />
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
